<template>
  <b-card no-body class="candidate-card" @click="$emit('view', candidate.id)">
    <div class="candidate-card__header">
      <div class="candidate-card__heading">
        <h5 class="candidate-card__name">{{ candidate.name || '-' }}</h5>
        <span class="candidate-card__trade">{{ candidate.trade || '-' }}</span>
      </div>
      <favourite-toggle
        :tradie-id="candidate.id"
        :candidate="candidate"
        class="candidate-card__favourite"
        @error="$emit('error', $event)"
      />
    </div>

    <!-- Only ever true on In Progress / Pools / Favourites — the browse/
         search list only ever returns otw-enabled candidates in the first
         place, so this never shows there. Surfaces that the relationship
         (accepted resume, saved candidate) is still valid, but the tradie
         has since gone inactive — rather than that silently being invisible
         to whoever's deciding whether to send a workforce invitation. -->
    <div
      v-if="candidate.open_to_work_enabled === false"
      class="candidate-card__inactive-notice"
    >
      <b-icon icon="exclamation-triangle-fill" class="mr-1" />
      No longer open to work
    </div>

    <div class="candidate-card__meta">
      <span v-if="candidate.experience_band" class="candidate-card__meta-item">
        <b-icon icon="award" class="mr-1" />
        {{ candidate.experience_band }}
      </span>
      <span v-if="candidate.location" class="candidate-card__meta-item">
        <b-icon icon="geo-alt" class="mr-1" />
        {{ candidate.location }}
      </span>
      <span v-if="candidate.availability" class="candidate-card__meta-item">
        <b-icon icon="calendar-check" class="mr-1" />
        {{ candidate.availability }}
      </span>
    </div>

    <div class="candidate-card__status-row">
      <compliance-status-badge
        v-if="!plainFiles"
        :status="candidate.compliance_status"
      />
      <labeled-status-badge
        label="Has Resume"
        :value="candidate.resume_available ? 'Yes' : 'No'"
        :variant="candidate.resume_available ? 'success' : 'secondary'"
      />
    </div>

    <!-- Discover: just the names of the files they hold, no status. -->
    <div v-if="plainFiles" class="candidate-card__files">
      <span class="candidate-card__files-label">Files</span>
      <div v-if="!fileNames.length" class="candidate-card__files-none">
        None listed
      </div>
      <ul v-else class="candidate-card__files-list">
        <li v-for="name in fileNames" :key="name">{{ name }}</li>
      </ul>
    </div>
    <key-ticket-pills
      v-else-if="!plainFiles"
      :tickets="candidate.key_tickets"
      class="candidate-card__tickets"
    />

    <div class="candidate-card__spacer"></div>

    <div class="candidate-card__actions" @click.stop>
      <div v-if="showResumeGroup" class="candidate-card__action-group">
        <span class="candidate-card__action-label">Resume Request</span>
        <resume-request-button
          :tradie-id="candidate.id"
          :status="candidate.resume_request_status"
          :retryable="candidate.resume_request_retryable"
          @request="$emit('request-resume', candidate.id)"
        />
      </div>
      <div v-if="showWorkforceGroup" class="candidate-card__action-group">
        <span class="candidate-card__action-label">Workforce Invitation</span>
        <workforce-invite-button
          :resume-request-status="candidate.resume_request_status"
          :workforce-invitation-status="candidate.workforce_invitation_status"
          @invite="$emit('invite-workforce', candidate.id)"
          @apply-token="
            $emit('apply-token', {
              invitationId: candidate.workforce_invitation_id,
              tradieId: candidate.id,
              tradieName: candidate.name,
            })
          "
        />
      </div>
    </div>
  </b-card>
</template>

<script>
import ComplianceStatusBadge from './ComplianceStatusBadge.vue'
import LabeledStatusBadge from './LabeledStatusBadge.vue'
import KeyTicketPills from './KeyTicketPills.vue'
import ResumeRequestButton from './ResumeRequestButton.vue'
import WorkforceInviteButton from './WorkforceInviteButton.vue'
import FavouriteToggle from './FavouriteToggle.vue'

// D9: dedicated card component for the OTW browse grid, not the existing
// table pattern from CompanyTradieList.vue (recruiting-marketplace UX).
export default {
  name: 'CandidateCard',

  components: {
    ComplianceStatusBadge,
    LabeledStatusBadge,
    KeyTicketPills,
    ResumeRequestButton,
    WorkforceInviteButton,
    FavouriteToggle,
  },

  props: {
    candidate: {
      type: Object,
      required: true,
    },
    // Open to Work (browse) only lets a company request a resume — inviting
    // to the workforce happens once that request is accepted, tracked on the
    // Candidates (in-progress) tab instead.
    showWorkforceInvite: {
      type: Boolean,
      default: true,
    },
    // On the Pipeline tab a candidate is only ever in one phase at a time —
    // show just that phase's CTA (resume until it's accepted, then
    // workforce) instead of both groups at once.
    pipelineView: {
      type: Boolean,
      default: false,
    },
    // Discover: no compliance status, and the files they hold are listed by
    // name only (no verified / expiring colouring).
    plainFiles: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    fileNames() {
      return (this.candidate.key_tickets || [])
        .map((t) => t && t.name)
        .filter(Boolean)
    },

    resumeAccepted() {
      return this.candidate.resume_request_status === 'accepted'
    },

    // Once a workforce invitation exists the resume phase is behind them,
    // even if the resume request itself never reached "accepted".
    inWorkforcePhase() {
      return this.resumeAccepted || !!this.candidate.workforce_invitation_status
    },

    showResumeGroup() {
      return this.pipelineView ? !this.inWorkforcePhase : true
    },

    showWorkforceGroup() {
      return this.pipelineView
        ? this.inWorkforcePhase
        : this.showWorkforceInvite
    },
  },
}
</script>

<style lang="scss" scoped>
.candidate-card {
  cursor: pointer;
  // Fills the height of its grid column (the column itself stretches to
  // match the tallest card in its row — see the `align-v` removal on the
  // b-row in ListPage.vue) so every card in a row is the same height
  // instead of only as tall as its own content.
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1.1rem;
  border-radius: 10px;
  border: 1px solid #e1e6ea;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    border-color: #cfd6dd;
  }
}

.candidate-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.candidate-card__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.candidate-card__name {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.candidate-card__trade {
  color: #6c7581;
  font-size: 0.85rem;
}

.candidate-card__favourite {
  flex-shrink: 0;
}

// Same amber "warning" palette as the token_pending status badge elsewhere
// in this feature (see .otw-status-badge--token_pending), for one consistent
// "needs your attention" colour across Open to Work rather than introducing
// a new one just for this notice.
.candidate-card__inactive-notice {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #8a6a16;
  background-color: #fdf6e3;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
}

.candidate-card__meta {
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: #495057;
}

.candidate-card__meta-item {
  display: flex;
  align-items: center;
}

.candidate-card__status-row {
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.candidate-card__tickets {
  margin-top: 0.65rem;
}

.candidate-card__files {
  margin-top: 0.85rem;
  font-size: 0.82rem;
  color: #495057;
}

.candidate-card__files-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #97a1ac;
}

.candidate-card__files-none {
  margin-top: 0.25rem;
  color: #97a1ac;
}

.candidate-card__files-list {
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
}

// Absorbs any leftover height once the card is stretched to match a taller
// neighbour in the same row, so the actions row always sits flush with the
// card's bottom edge instead of floating partway down a tall card.
.candidate-card__spacer {
  flex: 1 1 auto;
}

.candidate-card__actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.candidate-card__action-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 auto;
  min-width: 0;
}

// Distinguishes the two independent pipelines each card can show at once
// (resume-sharing vs. workforce-hiring) — without this, a resume-status
// button and a workforce-status button sitting side by side with no
// context read as one undifferentiated row of buttons.
.candidate-card__action-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #97a1ac;
}
</style>
